import apiClient from '../config/api';
import {
  ExpenseRequestDTO,
  ExpenseResponseDTO,
  FichierResponseDTO,
  PresignedUrlDTO,
  ExpenseStatus,
} from '../types/expense.types';
import { AxiosProgressEvent } from 'axios';

/**
 * Options pour l'upload de fichiers
 */
export interface UploadOptions {
  onProgress?: (progressEvent: AxiosProgressEvent) => void;
  signal?: AbortSignal;
}

/**
 * Service de gestion des dépenses
 */
class ExpenseService {
  /**
   * Créer une nouvelle dépense
   */
  async create(data: ExpenseRequestDTO): Promise<ExpenseResponseDTO> {
    console.log('📝 Création d\'une dépense:', data);
    const response = await apiClient.post<ExpenseResponseDTO>('/api/expenses', data);
    console.log('✅ Dépense créée avec succès:', response.data);
    return response.data;
  }

  /**
   * Récupérer toutes les dépenses de l'utilisateur
   */
  async getAll(): Promise<ExpenseResponseDTO[]> {
    console.log('📥 Récupération de toutes les dépenses');
    const response = await apiClient.get<ExpenseResponseDTO[]>('/api/expenses');
    console.log(`✅ ${response.data.length} dépense(s) récupérée(s)`);
    return response.data;
  }

  /**
   * Récupérer une dépense par son ID
   */
  async getById(id: number): Promise<ExpenseResponseDTO> {
    console.log(`📥 Récupération de la dépense #${id}`);
    const response = await apiClient.get<ExpenseResponseDTO>(`/api/expenses/${id}`);
    console.log('✅ Dépense récupérée:', response.data);
    return response.data;
  }

  /**
   * Récupérer les dépenses d'un projet
   */
  async getByProject(projetId: number): Promise<ExpenseResponseDTO[]> {
    console.log(`📥 Récupération des dépenses du projet #${projetId}`);
    const response = await apiClient.get<ExpenseResponseDTO[]>(`/api/expenses/projet/${projetId}`);
    console.log(`✅ ${response.data.length} dépense(s) récupérée(s)`);
    return response.data;
  }

  /**
   * Récupérer les dépenses d'un mois
   */
  async getByMonth(year: number, month: number): Promise<ExpenseResponseDTO[]> {
    console.log(`📥 Récupération des dépenses de ${month}/${year}`);
    const response = await apiClient.get<ExpenseResponseDTO[]>(
      `/api/expenses/month?year=${year}&month=${month}`
    );
    console.log(`✅ ${response.data.length} dépense(s) récupérée(s)`);
    return response.data;
  }

  /**
   * Récupérer les dépenses d'un projet pour un mois
   */
  async getByProjectAndMonth(
    projetId: number,
    year: number,
    month: number
  ): Promise<ExpenseResponseDTO[]> {
    console.log(`📥 Récupération des dépenses du projet #${projetId} pour ${month}/${year}`);
    const response = await apiClient.get<ExpenseResponseDTO[]>(
      `/api/expenses/projet/${projetId}/month?year=${year}&month=${month}`
    );
    console.log(`✅ ${response.data.length} dépense(s) récupérée(s)`);
    return response.data;
  }

  /**
   * Récupérer les dépenses dans une plage de dates
   */
  async getByDateRange(startDate: string, endDate: string): Promise<ExpenseResponseDTO[]> {
    console.log(`📥 Récupération des dépenses entre ${startDate} et ${endDate}`);
    const response = await apiClient.get<ExpenseResponseDTO[]>(
      `/api/expenses/range?startDate=${startDate}&endDate=${endDate}`
    );
    console.log(`✅ ${response.data.length} dépense(s) récupérée(s)`);
    return response.data;
  }

  /**
   * Récupérer les dépenses par statut
   */
  async getByStatus(status: ExpenseStatus): Promise<ExpenseResponseDTO[]> {
    console.log(`📥 Récupération des dépenses avec le statut ${status}`);
    const response = await apiClient.get<ExpenseResponseDTO[]>(`/api/expenses/status/${status}`);
    console.log(`✅ ${response.data.length} dépense(s) récupérée(s)`);
    return response.data;
  }

  /**
   * Mettre à jour une dépense
   */
  async update(id: number, data: Partial<ExpenseRequestDTO>): Promise<ExpenseResponseDTO> {
    console.log(`📝 Mise à jour de la dépense #${id}:`, data);
    const response = await apiClient.put<ExpenseResponseDTO>(`/api/expenses/${id}`, data);
    console.log('✅ Dépense mise à jour avec succès:', response.data);
    return response.data;
  }

  /**
   * Supprimer une dépense
   */
  async delete(id: number): Promise<void> {
    console.log(`🗑️ Suppression de la dépense #${id}`);
    await apiClient.delete(`/api/expenses/${id}`);
    console.log('✅ Dépense supprimée avec succès');
  }

  // ========================================
  // Upload de Justificatifs
  // ========================================

  /**
   * Uploader un justificatif pour une dépense
   */
  async uploadReceipt(
    expenseId: number,
    file: File,
    options?: UploadOptions
  ): Promise<FichierResponseDTO> {
    console.log('📤 Upload d\'un justificatif pour la dépense #', expenseId, ':', {
      name: file.name,
      size: file.size,
      type: file.type,
    });

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await apiClient.post<FichierResponseDTO>(
        `/api/expenses/${expenseId}/receipt`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: options?.onProgress,
          signal: options?.signal,
        }
      );

      console.log('✅ Justificatif uploadé avec succès:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ Erreur lors de l\'upload du justificatif:', error);
      throw error;
    }
  }

  /**
   * Récupérer les justificatifs d'une dépense
   */
  async getReceipts(expenseId: number): Promise<FichierResponseDTO[]> {
    console.log(`📥 Récupération des justificatifs de la dépense #${expenseId}`);
    const response = await apiClient.get<FichierResponseDTO[]>(`/api/expenses/${expenseId}/receipts`);
    console.log(`✅ ${response.data.length} justificatif(s) récupéré(s)`);
    return response.data;
  }

  /**
   * Récupérer un fichier par son ID
   */
  async getFileById(fileId: number): Promise<FichierResponseDTO> {
    console.log(`📥 Récupération du fichier #${fileId}`);
    const response = await apiClient.get<FichierResponseDTO>(`/api/fichiers/${fileId}`);
    console.log('✅ Fichier récupéré:', response.data);
    return response.data;
  }

  /**
   * Obtenir une URL présignée pour accéder à un fichier
   */
  async getPresignedUrl(fileId: number): Promise<PresignedUrlDTO> {
    console.log(`📥 Génération d\'une URL présignée pour le fichier #${fileId}`);
    const response = await apiClient.get<PresignedUrlDTO>(`/api/fichiers/${fileId}/presigned-url`);
    console.log('✅ URL présignée générée, expire dans:', response.data.expiresIn, 'secondes');
    return response.data;
  }

  /**
   * Supprimer un fichier
   */
  async deleteFile(fileId: number): Promise<void> {
    console.log(`🗑️ Suppression du fichier #${fileId}`);
    await apiClient.delete(`/api/fichiers/${fileId}`);
    console.log('✅ Fichier supprimé avec succès');
  }

  // ========================================
  // Validation
  // ========================================

  /**
   * Valider un fichier avant upload
   */
  validateFile(file: File): boolean {
    // Taille maximale: 10MB
    const MAX_SIZE = 10 * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      throw new Error('La taille du fichier ne doit pas dépasser 10 MB');
    }

    // Types MIME autorisés
    const allowedTypes = [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/webp',
      'application/pdf',
    ];

    if (!allowedTypes.includes(file.type)) {
      throw new Error(
        `Type de fichier non autorisé. Types acceptés: ${allowedTypes.join(', ')}`
      );
    }

    return true;
  }

  /**
   * Formater la taille d'un fichier en format lisible
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }

  // ========================================
  // Export ZIP
  // ========================================

  /**
   * Télécharger l'export complet au format ZIP (CSV + justificatifs)
   * @param year Année (ex: 2026)
   * @param month Mois (1-12)
   * @returns Le fichier ZIP en tant que Blob
   */
  async downloadZipExport(year: number, month: number): Promise<Blob> {
    console.log(`📦 Téléchargement de l'export ZIP pour ${month}/${year}`);

    // Formater la période au format YYYY-MM
    const period = `${year}-${String(month).padStart(2, '0')}`;

    const response = await apiClient.get(`/api/expenses/export/zip`, {
      params: { period },
      responseType: 'blob', // Important pour recevoir un fichier binaire
    });

    console.log('✅ Export ZIP téléchargé avec succès');
    return response.data;
  }

  /**
   * Déclencher le téléchargement d'un fichier Blob dans le navigateur
   * @param blob Le fichier à télécharger
   * @param filename Le nom du fichier
   */
  downloadBlobAsFile(blob: Blob, filename: string): void {
    // Créer un lien temporaire
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;

    // Déclencher le téléchargement
    document.body.appendChild(link);
    link.click();

    // Nettoyer
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    console.log(`✅ Fichier ${filename} téléchargé avec succès`);
  }
}

export default new ExpenseService();
